import { NavLink, Outlet, useLocation, Navigate } from "react-router-dom";
import { Sparkles, Users, DollarSign, FlaskConical, HeartHandshake, MessagesSquare } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Brief 3.5 PR 2 §1.4 — Nerea sub-shell.
 *
 * Collapses the previous 6 top-level left-rail Nerea entries
 * (Autonomy / Roster / Eval / Usage / Rehearsal / Reassurance)
 * into a single nav entry that lands here. Each surface keeps its
 * own URL (`/admin/nerea/autonomy`, `/admin/nerea/roster`, etc.) so
 * link rot is zero — this shell only renders a tab-bar above the
 * existing pages.
 *
 * Visibility:
 *   • Autonomy / Roster / Eval / Usage are visible to all admin
 *     team roles (existing behaviour).
 *   • Rehearsal + Reassurance are super_admin only (existing).
 */
const TABS = [
  { key: "autonomy",      label: "Autonomy",      icon: Sparkles,        to: "/admin/nerea/autonomy",      superAdminOnly: false },
  { key: "roster",        label: "Roster",        icon: Users,           to: "/admin/nerea/roster",        superAdminOnly: false },
  { key: "usage",         label: "Usage",         icon: DollarSign,      to: "/admin/nerea/usage",         superAdminOnly: false },
  { key: "conversations", label: "Conversations", icon: MessagesSquare,  to: "/admin/nerea/conversations", superAdminOnly: false },
  { key: "rehearsal",     label: "Rehearsal",     icon: FlaskConical,    to: "/admin/ops/autonomy-rehearsal", superAdminOnly: true  },
  { key: "reassurance",   label: "Reassurance",   icon: HeartHandshake,  to: "/admin/ops/reassurance",     superAdminOnly: true  },
];

export default function AdminNereaShell() {
  const { user } = useAuth();
  const location = useLocation();

  // Bare `/admin/nerea` → redirect to first visible tab.
  if (location.pathname === "/admin/nerea") {
    return <Navigate to="/admin/nerea/autonomy" replace />;
  }

  const visible = TABS.filter((t) => !t.superAdminOnly || user?.role === "super_admin");

  return (
    <div data-testid="nerea-shell" className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">— Nerea</div>
        <h1 className="text-[22px] tracking-tight text-white font-medium">AI co-pilot</h1>
      </div>

      <nav
        role="tablist"
        aria-label="Nerea surfaces"
        className="flex items-center gap-1 border-b border-white/[0.06] overflow-x-auto"
        data-testid="nerea-shell-tabs"
      >
        {visible.map(({ key, label, icon: Icon, to }) => (
          <NavLink
            key={key}
            to={to}
            role="tab"
            data-testid={`nerea-shell-tab-${key}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] -mb-px border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? "border-cyan-400/70 text-white"
                  : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`
            }
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
